/**
 * Neon Auth Client Configuration
 * Serverless authentication powered by Better Auth via @neondatabase/neon-js
 *
 * Auth URL: https://ep-odd-dawn-aggx168n.neonauth.c-2.eu-central-1.aws.neon.tech/neondb/auth
 * JWKS URL: https://ep-odd-dawn-aggx168n.neonauth.c-2.eu-central-1.aws.neon.tech/neondb/auth/.well-known/jwks.json
 *
 * Note: Auth endpoint must match database endpoint (ep-odd-dawn-aggx168n)
 *
 * Trusted domains:
 * - https://brandsyncup.com
 * - https://legalsyncup.com
 */

import { createClient } from '@neondatabase/neon-js';
import { BetterAuthReactAdapter } from '@neondatabase/neon-js/auth/react/adapters';
import { appConfig } from '@/config/environment';

/**
 * Database types for type-safe queries
 * TODO: Generate types using `npx neon-js gen-types --db-url "..."`
 */
export type Database = {
  public: {
    Tables: Record<string, never>;
    // Will be populated by type generation using:
    // npx neon-js gen-types --db-url "..."
  };
};

/**
 * Neon client with integrated authentication and data API
 * Uses BetterAuthReactAdapter for React hooks (useSession, etc.)
 *
 * @see https://github.com/neondatabase/neon-js
 */
export const neonClient = createClient<Database>({
  auth: {
    adapter: BetterAuthReactAdapter(),
    url: appConfig.NEON_AUTH_URL,
  },
  dataApi: {
    url: `${appConfig.NEON_AUTH_URL.replace('/auth', '')}/rest/v1`,
  },
});

/**
 * Authentication API
 * Provides Better Auth methods adapted for React
 */
export const auth = neonClient.auth;

/**
 * Sign in methods
 */
export const signIn = {
  /**
   * Sign in with email and password
   */
  email: (params: { email: string; password: string }) =>
    neonClient.auth.signIn.email(params),

  /**
   * Sign in with OAuth provider
   */
  social: (params: { provider: 'google' | 'github' | 'microsoft'; callbackURL?: string }) =>
    neonClient.auth.signIn.social(params),
};

/**
 * Sign up with email and password
 */
export const signUp = {
  email: (params: { email: string; password: string; name?: string }) =>
    neonClient.auth.signUp.email({ ...params, name: params.name || '' }),
};

/**
 * Sign out the current user
 */
export const signOut = () => neonClient.auth.signOut();

/**
 * Get the current session (async)
 */
export const getSession = () => neonClient.auth.getSession();

/**
 * React hook for session state
 * Returns { data, isPending, error }
 */
export const useSession = () => neonClient.auth.useSession();

/**
 * Database query helper
 * Provides PostgREST-compatible query builder with automatic token injection
 *
 * @example
 * const { data } = await from('users').select('*').eq('status', 'active');
 */
export const from = <T extends string>(table: T) => neonClient.from(table);

/**
 * RPC helper for calling stored procedures
 *
 * @example
 * const { data } = await rpc('get_user_stats', { user_id: 123 });
 */
export const rpc = <T extends string>(
  fn: T,
  params?: Record<string, unknown>
) => neonClient.rpc(fn, params);

/**
 * Types for auth
 */
export type NeonAuthSession = Awaited<ReturnType<typeof getSession>>;
export type NeonAuthUser = {
  id: string;
  email?: string;
  name: string;
  emailVerified: boolean;
  image?: string;
  createdAt: Date;
  updatedAt: Date;
  banned: boolean;
  role?: string;
  banReason?: string;
  banExpires?: Date;
};

export default neonClient;
